// BackgroundProvider — store, scheduler, resolver, and hooks.
// Unified layer model: every background has `layers: [...]` (1 for plain image/gradient, 2–6 for a scene).
// Legacy single-image/gradient bgs are normalized to a 3-layer auto-parallax (back/mid/front).

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import seedBackgrounds from './seedBackgrounds';
import { API_URL } from '../../api/api';
import {
  isIdbSrc,
  idbKey,
  getBlob,
  syncToBackend,
  resolveToUrl,
  getCachedUrl,
  getAllKeys,
  deleteBlob,
  mediaSrcToUrl,
} from './mediaStore';

// Only small UI preferences live in localStorage now — the scene library and
// all media are persisted server-side (GET/POST/PUT/DELETE /scenes + /media),
// which permanently fixes the old QuotaExceededError that silently dropped
// new scenes and uploaded images.
const STORAGE_KEY = 'emhub_bg_prefs_v3';
const SCENES_URL = `${API_URL}/api/settings/backgrounds/scenes`;
const SETTINGS_URL = `${API_URL}/api/settings/backgrounds/settings`;
const isSeedId = (id) => typeof id === 'string' && id.startsWith('seed-');

// Turn any local/blob/data/idb media reference into a durable server URL.
// Remote URLs and gradient strings pass through untouched.
async function persistSrc(src) {
  if (!src || typeof src !== 'string') return src;
  if (/^https?:\/\//i.test(src)) return src;
  if (/^(linear|radial|conic)-gradient/i.test(src)) return src;
  let blob = null;
  let key = null;
  if (isIdbSrc(src)) {
    key = idbKey(src);
    blob = await getBlob(key);
  } else if (src.startsWith('data:')) {
    try {
      blob = await (await fetch(src)).blob();
    } catch {
      /* ignore */
    }
    key = 'bg-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  } else {
    return src;
  }
  if (!blob || !key) return src;
  const data = await syncToBackend(key, blob);
  return data?.url || src;
}

// Replace every media reference inside a scene object with server URLs.
async function materializeMedia(obj) {
  const out = { ...obj };
  out.src = await persistSrc(obj.src);
  out.thumb = obj.thumb ? await persistSrc(obj.thumb) : out.src;
  if (Array.isArray(obj.layers)) {
    out.layers = await Promise.all(
      obj.layers.map(async (l) => ({ ...l, src: await persistSrc(l.src) }))
    );
  }
  return out;
}

async function authedFetch(url, opts = {}) {
  const token = localStorage.getItem('token');
  const headers = { ...(opts.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(url, { ...opts, headers });
}

// Persist org-wide settings (active default / employee default / parallax).
// Fire-and-forget — the local state already updated optimistically.
function saveSettings(patch) {
  return authedFetch(SETTINGS_URL, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  }).catch((e) => console.warn('[backgrounds] settings save failed', e));
}

// ---------- layer defaults ----------
export const LAYER_DEFAULTS = {
  depth: 60,
  scale: 110,
  rotate: 0,
  blur: 0,
  opacity: 100,
  blend: 'normal',
  visible: true,
  offsetX: 0,
  offsetY: 0,
};

export function makeLayer(src, patch = {}) {
  return {
    id: 'l-' + Math.random().toString(36).slice(2, 9),
    src,
    ...LAYER_DEFAULTS,
    ...patch,
  };
}

// ---------- migration ----------
// Legacy `kind:'auto'` (3-layer parallax fan) → new `kind:'static'`
// (single, non-parallax layer with per-image blur/scale/rotate/offset).
// Layers whose src is a data: URL were written by the old SceneBuilder code
// path that exceeded localStorage quota on save. Those backgrounds never
// round-tripped successfully — drop them on load so the store stays clean.
function isDataUrlSrc(src) {
  return typeof src === 'string' && src.startsWith('data:');
}

function migrate(st) {
  if (!st) return st;
  st.backgrounds = (st.backgrounds || [])
    .map((b) => {
      let next = b;
      // Strip dead data-URL layers from any existing background.
      if (next.layers && next.layers.length) {
        const cleaned = next.layers.filter((l) => !isDataUrlSrc(l.src));
        if (cleaned.length !== next.layers.length) {
          next = { ...next, layers: cleaned };
        }
      }
      if (!next.layers || !next.layers.length) {
        const s = next.settings || {};
        const common = { scale: s.scale || 110, blur: s.blur || 0 };
        // Fall back to top-level src only if it isn't itself a data URL.
        const fallback = isDataUrlSrc(next.src) ? null : next.src;
        next = {
          ...next,
          layers: fallback ? [makeLayer(fallback, { ...common })] : [],
        };
      }
      // Normalize legacy kinds to static, unless it's a scene or video.
      if (!next.kind || next.kind === 'auto' || next.kind === 'image') {
        next = { ...next, kind: 'static' };
      }
      return next;
    })
    // A scene/static bg with no layers left is effectively broken — drop it.
    .filter((b) => b.layers && b.layers.length > 0);
  // Retire old default id if gradient seed was removed.
  if (!st.backgrounds.find((b) => b.id === st.defaultId)) {
    st.defaultId = st.backgrounds[0]?.id || null;
  }
  return st;
}

// Minimal in-page toast for storage errors so quota overflows stop being invisible.
function showBgToast(message) {
  if (typeof document === 'undefined') return;
  try {
    const existing = document.getElementById('emhub-bg-toast');
    if (existing) existing.remove();
    const el = document.createElement('div');
    el.id = 'emhub-bg-toast';
    el.textContent = message;
    el.style.cssText =
      'position:fixed;right:24px;bottom:24px;z-index:99999;' +
      'max-width:360px;padding:12px 16px;border-radius:10px;' +
      'background:#991b1b;color:#fff;' +
      'font:500 13px/1.4 system-ui,sans-serif;' +
      'box-shadow:0 12px 32px rgba(0,0,0,.25);';
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 6000);
  } catch (e) {
    // swallow — toast is best-effort
  }
}

// The built-in library minus the org's hidden ids, normalised the same way
// loadState does. Rebuilt from the constant (never subtracted from whatever is
// in state) so restoring a hidden built-in on one device brings it back on
// every other one, instead of it staying gone until localStorage is cleared.
function seedLibrary(hidden = []) {
  const list = seedBackgrounds
    .filter((b) => !hidden.includes(b.id))
    .map((b) => ({ ...b }));
  return migrate({ backgrounds: list, defaultId: null }).backgrounds;
}

function loadState() {
  // Backgrounds always start from the built-in seed library; server scenes are
  // merged in on mount. Only small prefs come from localStorage.
  const base = {
    backgrounds: seedBackgrounds.map((b) => ({ ...b })),
    defaultId: 'seed-mountain-dawn',
    employeeDefaultId: null,
    parallaxEnabled: true,
    respectReducedMotion: true,
    role: 'admin',
    hiddenSeedIds: [],
  };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const saved = JSON.parse(raw) || {};
      if (saved.defaultId !== undefined) base.defaultId = saved.defaultId;
      if (saved.employeeDefaultId !== undefined) base.employeeDefaultId = saved.employeeDefaultId;
      if (saved.parallaxEnabled !== undefined) base.parallaxEnabled = saved.parallaxEnabled;
      if (saved.respectReducedMotion !== undefined) base.respectReducedMotion = saved.respectReducedMotion;
      if (saved.role !== undefined) base.role = saved.role;
      if (Array.isArray(saved.hiddenSeedIds)) base.hiddenSeedIds = saved.hiddenSeedIds;
    }
  } catch (e) {}
  // Drop any seeds the user has deleted (persisted in hiddenSeedIds) up front
  // so they don't flash before the server settings load.
  if (base.hiddenSeedIds.length) {
    base.backgrounds = seedLibrary(base.hiddenSeedIds);
  }
  return migrate(base);
}

// ---------- resolver helpers ----------
export function inYearlyRange(now, mmddStart, mmddEnd) {
  if (!mmddStart || !mmddEnd) return false;
  const y = now.getFullYear();
  const [sm, sd] = mmddStart.split('-').map(Number);
  const [em, ed] = mmddEnd.split('-').map(Number);
  let start = new Date(y, sm - 1, sd, 0, 0, 0);
  let end = new Date(y, em - 1, ed, 23, 59, 59);
  if (end < start) {
    if (now >= start) end = new Date(y + 1, em - 1, ed, 23, 59, 59);
    else start = new Date(y - 1, sm - 1, sd, 0, 0, 0);
  }
  return now >= start && now <= end;
}

export function inOnceRange(now, start, end) {
  if (!start || !end) return false;
  return now >= new Date(start + 'T00:00:00') && now <= new Date(end + 'T23:59:59');
}

export function inTimeOfDay(now, tStart, tEnd) {
  if (!tStart || !tEnd) return false;
  const mins = now.getHours() * 60 + now.getMinutes();
  const [sh, sm] = tStart.split(':').map(Number);
  const [eh, em] = tEnd.split(':').map(Number);
  const s = sh * 60 + sm;
  const e = eh * 60 + em;
  return s <= e ? mins >= s && mins <= e : mins >= s || mins <= e;
}

export function isActiveSchedule(b, now) {
  const s = b.schedule || {};
  if (s.kind === 'once') return inOnceRange(now, s.start, s.end);
  if (s.kind === 'yearly') return inYearlyRange(now, s.start, s.end);
  if (s.kind === 'timeOfDay') return inTimeOfDay(now, s.timeStart, s.timeEnd);
  return false;
}

export function resolveActive(st, now) {
  const scheduled = st.backgrounds
    .filter((b) => isActiveSchedule(b, now))
    .sort((a, b) => (b.priority || 0) - (a.priority || 0));
  if (scheduled[0]) return { bg: scheduled[0], reason: 'scheduled' };
  const defId = st.employeeDefaultId || st.defaultId;
  const def = st.backgrounds.find((b) => b.id === defId);
  if (def)
    return {
      bg: def,
      reason: st.employeeDefaultId ? 'employee-default' : 'admin-default',
    };
  return { bg: st.backgrounds[0], reason: 'fallback' };
}

export function upcomingSchedule(st, now, limit = 10) {
  const out = [];
  st.backgrounds.forEach((b) => {
    const s = b.schedule || {};
    if (s.kind === 'once' && s.start) {
      const start = new Date(s.start + 'T00:00:00');
      const end = new Date(s.end + 'T23:59:59');
      if (end >= now) out.push({ bg: b, start, end, kind: 'once' });
    } else if (s.kind === 'yearly' && s.start && s.end) {
      const y = now.getFullYear();
      for (const yr of [y, y + 1]) {
        const [sm, sd] = s.start.split('-').map(Number);
        const [em, ed] = s.end.split('-').map(Number);
        let start = new Date(yr, sm - 1, sd);
        let end = new Date(yr, em - 1, ed, 23, 59, 59);
        if (end < start) end = new Date(yr + 1, em - 1, ed, 23, 59, 59);
        if (end >= now) {
          out.push({ bg: b, start, end, kind: 'yearly' });
          break;
        }
      }
    } else if (s.kind === 'timeOfDay' && s.timeStart && s.timeEnd) {
      const [sh, sm] = s.timeStart.split(':').map(Number);
      const [eh, em] = s.timeEnd.split(':').map(Number);
      const d = new Date(now);
      d.setHours(sh, sm, 0, 0);
      const e = new Date(now);
      e.setHours(eh, em, 0, 0);
      if (e < d) e.setDate(e.getDate() + 1);
      if (e >= now) out.push({ bg: b, start: d, end: e, kind: 'timeOfDay' });
    }
  });
  return out.sort((a, b) => a.start - b.start).slice(0, limit);
}

// ---------- render layers ----------
// - scene: user-authored layer stack (parallax on).
// - static: single image layer, no parallax (depth 0 tells renderer to skip
//   mouse multiplier). User controls blur/scale/rotate/offset per-layer.
// - video: single media layer; renderer uses <video> for mediaType 'video',
//   background-image for 'gif'. No parallax.
export function computeRenderLayers(bg) {
  if (!bg?.layers?.length) return [];
  if (bg.kind === 'scene') return bg.layers.filter((l) => l.visible !== false);
  // static / video / unknown: return the single layer, flagged non-parallax.
  const base = bg.layers[0];
  return [{ ...base, depth: 0 }];
}

export function layerBgValue(src) {
  if (!src) return 'transparent';
  const s = String(src);
  if (s.startsWith('linear-gradient') || s.startsWith('radial-gradient')) return s;
  if (s.startsWith('#') || s.startsWith('rgb') || s.startsWith('hsl')) return s;
  return `url("${s}") center/cover no-repeat`;
}

// ---------- context ----------
const BackgroundContext = createContext(null);

export function BackgroundProvider({ children }) {
  const [state, setState] = useState(loadState);
  // Map of indexeddb key -> resolved blob: URL. Rebuilt when refs change.
  const [mediaUrls, setMediaUrls] = useState({});
  // Have the org's background settings (hidden built-ins, default scene) been
  // fetched yet? Until they have, the local seed library on its own resolves to
  // the wrong picture: a scheduled seed such as 'seed-tod-morning' outranks the
  // default by priority, so the dashboard used to paint an unrelated stock photo
  // for a beat before the real background replaced it. That was most visible
  // right after login, because a 401 anywhere calls localStorage.clear() and
  // wipes the cached prefs (defaultId / hiddenSeedIds) that normally mask it.
  // No token means the login page, where there is nothing to fetch — the seeds
  // are all we will ever have, so treat that as hydrated straight away.
  const [hydrated, setHydrated] = useState(() => {
    try {
      return !localStorage.getItem('token');
    } catch (e) {
      return true;
    }
  });

  // Persist ONLY small prefs — never the (potentially huge) media library.
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          defaultId: state.defaultId,
          employeeDefaultId: state.employeeDefaultId,
          parallaxEnabled: state.parallaxEnabled,
          respectReducedMotion: state.respectReducedMotion,
          role: state.role,
          hiddenSeedIds: state.hiddenSeedIds || [],
        })
      );
    } catch (e) {
      console.error('[backgrounds] prefs persist failed:', e);
    }
  }, [
    state.defaultId,
    state.employeeDefaultId,
    state.parallaxEnabled,
    state.respectReducedMotion,
    state.role,
    state.hiddenSeedIds,
  ]);

  // Load the server-backed scene library and merge it with the built-in seeds.
  // The server is the source of truth for user-created scenes.
  //
  // Runs on mount AND on every auth change. Mount alone is not enough: this
  // provider sits above the router (App.js), so it mounts once — on the login
  // page, before a token exists. Both requests 401 there, leaving the library
  // as the 12 local built-ins with no server scenes, and that stale list used
  // to survive the client-side navigation into the app. A manual refresh then
  // mounted the provider with a token and produced a completely different
  // list (server scenes, built-ins minus the hidden ones) — the "many images
  // on login, one after refresh" bug.
  const loadSeq = useRef(0);
  const loadFromServer = useCallback(async () => {
    const seq = ++loadSeq.current;
    const stale = () => seq !== loadSeq.current;   // a newer load has started
    // Scene library
    try {
      const res = await authedFetch(SCENES_URL);
      if (res.ok) {
        const scenes = await res.json();
        if (!stale() && Array.isArray(scenes)) {
          setState((st) => {
            const seeds = (st.backgrounds || []).filter((b) => isSeedId(b.id));
            // Non-seed entries come wholly from this response, so scenes left
            // over from a previous session/user don't linger after re-login.
            return { ...st, backgrounds: [...seeds, ...scenes] };
          });
        }
      }
    } catch (e) {
      console.warn('[backgrounds] could not load scenes from server', e);
    }
    // Shared org-wide settings (default / employee default / parallax)
    try {
      const res = await authedFetch(SETTINGS_URL);
      if (res.ok) {
        const s = await res.json();
        if (!stale() && s) {
          setState((st) => {
            const hidden = Array.isArray(s.hiddenSeedIds)
              ? s.hiddenSeedIds
              : st.hiddenSeedIds || [];
            // Keep the seed objects already in state (a seed tweaked in the
            // edit drawer is local-only) and take fresh copies from the
            // constant for any that were hidden and have since come back.
            const inState = new Map(
              st.backgrounds.filter((b) => isSeedId(b.id)).map((b) => [b.id, b])
            );
            const seeds = seedLibrary(hidden).map((b) => inState.get(b.id) || b);
            return {
              ...st,
              defaultId: s.defaultId ?? st.defaultId,
              employeeDefaultId: s.employeeDefaultId ?? st.employeeDefaultId,
              parallaxEnabled:
                s.parallaxEnabled == null ? st.parallaxEnabled : s.parallaxEnabled,
              respectReducedMotion:
                s.respectReducedMotion == null
                  ? st.respectReducedMotion
                  : s.respectReducedMotion,
              hiddenSeedIds: hidden,
              // Rebuild the built-in half from code against the server's hidden
              // list — this both drops seeds deleted on another device and
              // brings back ones that were un-hidden there.
              backgrounds: [
                ...seeds,
                ...st.backgrounds.filter((b) => !isSeedId(b.id)),
              ],
            };
          });
        }
      }
    } catch (e) {
      console.warn('[backgrounds] could not load settings from server', e);
    }
    // Success or failure, we've done all we can — let the background render.
    if (!stale()) setHydrated(true);
  }, []);

  useEffect(() => {
    loadFromServer();
    // Login.js dispatches 'emhub:login' right after writing the token; the
    // 'storage' event covers a login/logout that happened in another tab.
    // (Logout itself hard-navigates to /login, which remounts everything.)
    // An auth change invalidates the settings resolved for the old session, so
    // hide the background again until the new ones land rather than showing the
    // previous user's (or the seed library's) pick in the meantime.
    const reload = () => {
      setHydrated(false);
      loadFromServer();
    };
    const onLogin = () => reload();
    const onStorage = (e) => {
      if (!e || !e.key || e.key === 'token') reload();
    };
    window.addEventListener('emhub:login', onLogin);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('emhub:login', onLogin);
      window.removeEventListener('storage', onStorage);
    };
  }, [loadFromServer]);

  // Whenever the set of indexeddb-backed layers changes, resolve blob URLs.
  useEffect(() => {
    let cancelled = false;
    const keys = new Set();
    state.backgrounds.forEach((b) =>
      (b.layers || []).forEach((l) => {
        if (isIdbSrc(l.src)) keys.add(idbKey(l.src));
      })
    );
    (async () => {
      const next = {};
      await Promise.all(
        Array.from(keys).map(async (k) => {
          const cached = getCachedUrl(k);
          if (cached) {
            next[k] = cached;
            return;
          }
          // Backgrounds are decorative — a media lookup that fails (IDB
          // closing during navigation, quota, private browsing) must degrade
          // to "no background", never reject and surface as an uncaught
          // runtime error over the whole app.
          try {
            const url = await resolveToUrl(k);
            if (url) next[k] = url;
          } catch (e) {
            console.warn('[backgrounds] could not resolve media', k, e?.message);
          }
        })
      );
      if (!cancelled) setMediaUrls(next);
    })();
    // Garbage-collect orphan IDB blobs from the LOCAL cache only. We must NOT
    // delete the backend /media copies here: scenes now reference media by
    // server URL (not by `indexeddb:` key), so every idb key looks "orphaned"
    // even while a live scene still points at its uploaded file. Server-side
    // media cleanup happens on explicit scene delete instead.
    (async () => {
      try {
        const allKeys = await getAllKeys();
        const orphans = allKeys.filter((k) => !keys.has(k));
        await Promise.all(orphans.map((k) => deleteBlob(k).catch(() => {})));
      } catch (e) {}
    })();
    return () => {
      cancelled = true;
    };
  }, [state.backgrounds]);

  // re-tick every 30s so scheduled/time-of-day resolves update
  const [tick, setTick] = useState(Date.now());
  useEffect(() => {
    const i = setInterval(() => setTick(Date.now()), 30000);
    return () => clearInterval(i);
  }, []);

  // Create → upload media to the server, POST the scene, append what comes back
  // (which carries durable server URLs). Returns the saved scene (or null).
  const addBackground = useCallback(async (b) => {
    try {
      const mat = await materializeMedia(b);
      const payload = {
        name: mat.name,
        type: mat.type,
        kind: mat.kind,
        mediaType: mat.mediaType,
        tags: mat.tags,
        priority: mat.priority,
        src: mat.src,
        thumb: mat.thumb,
        layers: mat.layers,
        settings: mat.settings,
        schedule: mat.schedule,
      };
      const res = await authedFetch(SCENES_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const saved = await res.json();
      setState((st) => ({ ...st, backgrounds: [...st.backgrounds, saved] }));
      return saved;
    } catch (e) {
      console.error('[backgrounds] create failed:', e);
      showBgToast(`Couldn't save background: ${e?.message || 'unknown error'}`);
      return null;
    }
  }, []);

  // Update → optimistic local merge, then PUT (server scenes only; seeds are
  // built-in and stay local). Re-materialises layer media if layers changed.
  const updateBackground = useCallback(async (id, patch) => {
    setState((st) => ({
      ...st,
      backgrounds: st.backgrounds.map((b) =>
        b.id === id
          ? {
              ...b,
              ...patch,
              settings: { ...(b.settings || {}), ...(patch.settings || {}) },
              schedule: { ...(b.schedule || {}), ...(patch.schedule || {}) },
              layers: patch.layers ? patch.layers : b.layers,
            }
          : b
      ),
    }));
    if (isSeedId(id)) return;
    try {
      const mat = patch.layers || patch.src || patch.thumb
        ? await materializeMedia(patch)
        : patch;
      const body = {};
      ['name', 'settings', 'schedule', 'layers', 'priority', 'tags', 'src', 'thumb', 'type', 'kind', 'mediaType'].forEach(
        (k) => {
          if (k in mat) body[k] = mat[k];
        }
      );
      const res = await authedFetch(`${SCENES_URL}/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const saved = await res.json();
        setState((st) => ({
          ...st,
          backgrounds: st.backgrounds.map((b) => (b.id === id ? saved : b)),
        }));
      }
    } catch (e) {
      console.error('[backgrounds] update failed:', e);
      showBgToast(`Couldn't update background: ${e?.message || 'unknown error'}`);
    }
  }, []);

  const deleteBackground = useCallback(async (id) => {
    let hiddenAfter = null;
    setState((st) => {
      const next = {
        ...st,
        backgrounds: st.backgrounds.filter((b) => b.id !== id),
      };
      if (next.defaultId === id) next.defaultId = null;
      if (next.employeeDefaultId === id) next.employeeDefaultId = null;
      // Built-in seeds can't be deleted server-side (they reload from code), so
      // record the id in hiddenSeedIds — persisted to the shared settings so it
      // stays gone across reloads/devices.
      if (isSeedId(id)) {
        hiddenAfter = Array.from(new Set([...(st.hiddenSeedIds || []), id]));
        next.hiddenSeedIds = hiddenAfter;
      }
      return next;
    });
    if (isSeedId(id)) {
      if (hiddenAfter) saveSettings({ hiddenSeedIds: hiddenAfter });
      return;
    }
    try {
      await authedFetch(`${SCENES_URL}/${id}`, { method: 'DELETE' });
    } catch (e) {
      console.warn('[backgrounds] delete failed:', e);
    }
  }, []);
  const setDefault = useCallback((id) => {
    setState((st) => ({ ...st, defaultId: id }));
    saveSettings({ defaultId: id });
  }, []);
  const setEmployeeDefault = useCallback((id) => {
    setState((st) => ({ ...st, employeeDefaultId: id }));
    saveSettings({ employeeDefaultId: id });
  }, []);
  const setParallaxEnabled = useCallback((v) => {
    setState((st) => ({ ...st, parallaxEnabled: v }));
    saveSettings({ parallaxEnabled: !!v });
  }, []);
  const setRole = useCallback(
    (role) => setState((st) => ({ ...st, role })),
    []
  );
  // Reset = back to the full built-in library. hiddenSeedIds has to be cleared
  // ON THE SERVER too, not just locally: it's an org-wide row, so a local-only
  // reset put the built-ins back on screen and then lost them again on the next
  // refresh, which is exactly the inconsistency this page suffered from.
  const resetAll = useCallback(async () => {
    localStorage.removeItem(STORAGE_KEY);
    setState(loadState());
    await saveSettings({ hiddenSeedIds: [] });
    loadFromServer();
  }, [loadFromServer]);

  const active = useMemo(
    () =>
      hydrated
        ? resolveActive(state, new Date(tick))
        : { bg: null, reason: 'loading' },
    [hydrated, state, tick]
  );

  const resolveSrc = useCallback(
    (src) => {
      if (!src) return src;
      if (isIdbSrc(src)) return mediaUrls[idbKey(src)] || null;
      // Server-stored media is a root-relative path; join it to the public
      // uploads base (and repair the legacy prefix-less absolute URLs).
      return mediaSrcToUrl(src);
    },
    [mediaUrls]
  );

  const value = useMemo(
    () => ({
      state,
      active,
      hydrated,
      mediaUrls,
      resolveSrc,
      addBackground,
      updateBackground,
      deleteBackground,
      setDefault,
      setEmployeeDefault,
      setParallaxEnabled,
      setRole,
      resetAll,
    }),
    [
      state,
      active,
      hydrated,
      mediaUrls,
      resolveSrc,
      addBackground,
      updateBackground,
      deleteBackground,
      setDefault,
      setEmployeeDefault,
      setParallaxEnabled,
      setRole,
      resetAll,
    ]
  );

  return (
    <BackgroundContext.Provider value={value}>
      {children}
    </BackgroundContext.Provider>
  );
}

export function useBackgroundStore() {
  const ctx = useContext(BackgroundContext);
  if (!ctx) throw new Error('useBackgroundStore must be used inside <BackgroundProvider>');
  return ctx;
}

export function useActiveBackground() {
  return useBackgroundStore().active;
}
